import { graphql } from "./generated/index.js"

// Queries ======================================================================

// The document `records.update` reads through, so it must select EVERY owned-row
// relation. computeRelationChanges derives "the current set to diff against" from
// record[relationship]; an unselected relation reads as empty, which turns a
// reconcile into an append and a clear into a silent no-op. That was invisible
// while three of the six relationships had no write handler — a throw is louder
// than a half-done write — so it surfaced only once they did.
export const fetchRecordById = graphql(`
  query fetchRecordById($id: Int!) {
    record: records_by_pk(id: $id) {
      ...FullRecordFields
    }
  }
`)

// A record by its (type, slug) — the address in a record page's URL. Same
// selection as fetchRecordById.
export const fetchRecordBySlug = graphql(`
  query fetchRecordBySlug($type: String!, $slug: String!) {
    records(where: { type: { _eq: $type }, slug: { _eq: $slug } }, limit: 1) {
      ...FullRecordFields
    }
  }
`)

export const searchRecords = graphql(`
  query searchRecords($query: String!, $type: String, $limit: Int = 20) {
    records(
      where: { _and: [{ type: { _eq: $type } }, { _or: [{ name: { _ilike: $query } }] }] }
      limit: $limit
      order_by: [{ popularity: { score_10t: desc_nulls_last } }, { name: asc }]
    ) {
      ...RecordFields
      sources {
        ...RecordSourceFields
      }
      listings {
        ...RecordListingFields
        list {
          ...RecordFields
        }
      }
      taggings {
        ...RecordTaggingFields
      }
    }
  }
`)

export const searchAnyRecords = graphql(`
  query searchAnyRecords($query: String!, $limit: Int = 20) {
    records(
      where: { _or: [{ name: { _ilike: $query } }] }
      limit: $limit
      order_by: [{ popularity: { score_10t: desc_nulls_last } }, { name: asc }]
    ) {
      ...RecordFields
      sources {
        ...RecordSourceFields
      }
      listings {
        ...RecordListingFields
        list {
          ...RecordFields
        }
      }
      taggings {
        ...RecordTaggingFields
      }
    }
  }
`)

// A search inside a saved filter's matches: a filtered link field's picker.
export const searchFilteredRecords = graphql(`
  query searchFilteredRecords(
    $recordType: String!
    $filter: jsonb!
    $query: String!
    $limit: Int = 20
  ) {
    records: filter_records(
      args: { record_type: $recordType, filter: $filter }
      where: { name: { _ilike: $query } }
      limit: $limit
      order_by: [{ popularity: { score_10t: desc_nulls_last } }, { name: asc }]
    ) {
      ...RecordFields
      sources {
        ...RecordSourceFields
      }
      listings {
        ...RecordListingFields
        list {
          ...RecordFields
        }
      }
      taggings {
        ...RecordTaggingFields
      }
    }
  }
`)

// Which of these ids a saved filter matches: the check on a filtered link
// field's new targets.
export const fetchFilterMatchIds = graphql(`
  query fetchFilterMatchIds($recordType: String!, $filter: jsonb!, $ids: [Int!]!) {
    records: filter_records(
      args: { record_type: $recordType, filter: $filter }
      where: { id: { _in: $ids } }
    ) {
      id
    }
  }
`)

export const createRecord = graphql(`
  mutation createRecord(
    $type: String!
    $name: String!
    $slug: String
    $description: String
    $parent_id: Int
    $schema_id: Int
    $data: jsonb
    $visibility: String
  ) {
    insert_records_one(
      object: {
        type: $type
        name: $name
        slug: $slug
        description: $description
        parent_id: $parent_id
        schema_id: $schema_id
        data: $data
        visibility: $visibility
      }
    ) {
      ...RecordFields
    }
  }
`)

/**
 * Create a record and its relation rows in one document. Hasura wraps a nested
 * insert in a single transaction and fills each child's `record_id` from the
 * parent it just created — which is what lets `records.create` stamp relation
 * rows without knowing the id first.
 */
export const createRecordNested = graphql(`
  mutation createRecordNested($object: records_insert_input!) {
    insert_records_one(object: $object) {
      ...RecordFields
      links {
        ...RecordLinkFields
      }
      contacts {
        ...RecordContactFields
      }
      times {
        ...RecordTimeFields
      }
      taggings {
        ...RecordTaggingFields
      }
      attachments {
        ...RecordAttachmentFields
      }
      sources {
        ...RecordSourceFields
      }
    }
  }
`)

// Bulk sibling of createRecordNested: every record, with its nested rows, in
// one INSERT — one transaction, so the batch is all-or-nothing.
export const createRecordsNested = graphql(`
  mutation createRecordsNested($objects: [records_insert_input!]!) {
    insert_records(objects: $objects) {
      returning {
        ...RecordFields
        links {
          ...RecordLinkFields
        }
        contacts {
          ...RecordContactFields
        }
        times {
          ...RecordTimeFields
        }
        taggings {
          ...RecordTaggingFields
        }
        attachments {
          ...RecordAttachmentFields
        }
        sources {
          ...RecordSourceFields
        }
      }
    }
  }
`)

// One write across many records: `_set` for columns, `_append` for `data`
// keys. `_append` is jsonb `||` in Postgres, so each row's data is merged
// server-side — no client ever writes back a whole blob it read earlier.
export const updateRecordsMany = graphql(`
  mutation updateRecordsMany(
    $ids: [Int!]!
    $_set: records_set_input!
    $_append: records_append_input!
  ) {
    update_records(where: { id: { _in: $ids } }, _set: $_set, _append: $_append) {
      returning {
        ...RecordFields
      }
    }
  }
`)

export const updateRecordById = graphql(`
  mutation updateRecordById($id: Int!, $_set: records_set_input!) {
    update_records_by_pk(pk_columns: { id: $id }, _set: $_set) {
      ...RecordFields
    }
  }
`)

export const batchSearchRecordsByContact = graphql(`
  query batchSearchRecordsByContact($emails: [String!], $names: [String!], $type: String) {
    byEmail: record_contacts(
      where: {
        _and: [
          { value: { _in: $emails } }
          { contact_type: { _eq: "email" } }
          { record: { type: { _eq: $type } } }
        ]
      }
    ) {
      value
      record {
        ...RecordFields
      }
    }
    byName: records(where: { _and: [{ name: { _in: $names } }, { type: { _eq: $type } }] }) {
      ...RecordFields
    }
  }
`)

// Hard-delete a record through destroy_record, not delete_records_by_pk: the
// function suspends the audit triggers for the delete (they would otherwise log
// it against the row being removed and fail its FK), and the child rows go by
// their FKs' CASCADE / SET NULL. Exposed to site_admin only — records has no
// delete permission for any role.
export const destroyRecord = graphql(`
  mutation destroyRecord($id: Int!) {
    destroy_record(args: { p_id: $id }) {
      id
    }
  }
`)

// Fold one record into another. merge_records (postgres/functions/
// merge_records.sql) moves the source's rows onto the target and deletes the
// source, in one transaction; p_type is its guard that both records are that
// type. Returns the target.
export const mergeRecords = graphql(`
  mutation mergeRecords($sourceId: Int!, $targetId: Int!, $type: String!) {
    merge_records(args: { p_source_id: $sourceId, p_target_id: $targetId, p_type: $type }) {
      ...RecordFields
    }
  }
`)

// Archive/unarchive =============================================================
//
// A conditional WHERE, not a plain by-pk set: `archived_at: {_is_null: true}`
// (unarchive: `false`) makes the mutation itself idempotent, so a caller can't
// clobber the original archive timestamp by re-archiving an already-archived
// record, and a record already in the target state is untouched rather than
// re-stamped. RecordFields alone, not FullRecordFields: unlike update(),
// archiving changes no relation data, so paying for all six owned-row relation
// selections here — real payload weight on a bulk operation — would buy
// fidelity nothing here actually needs.

// Which of these ids exist right now — run strictly BEFORE the mutation (not
// alongside it), so the caller can distinguish "just archived"/"already was"
// from "never existed," and so a third party's write in between reads as
// "already in the target state" rather than "missing."
export const fetchRecordsArchivedState = graphql(`
  query fetchRecordsArchivedState($ids: [Int!]!) {
    records(where: { id: { _in: $ids } }) {
      id
      archived_at
    }
  }
`)

// archived_at is the literal "now()", evaluated by Postgres — the MCP's twin
// (ArchiveRecordsBatch) does the same, and a client-side new Date() drifted
// with the caller's clock, giving the same conceptual call different
// timestamps depending on which writer the caller reached.
export const archiveRecordsConditional = graphql(`
  mutation archiveRecordsConditional($ids: [Int!]!) {
    update_records(
      where: { id: { _in: $ids }, archived_at: { _is_null: true } }
      _set: { archived_at: "now()" }
    ) {
      returning {
        ...RecordFields
      }
    }
  }
`)

export const unarchiveRecordsConditional = graphql(`
  mutation unarchiveRecordsConditional($ids: [Int!]!) {
    update_records(
      where: { id: { _in: $ids }, archived_at: { _is_null: false } }
      _set: { archived_at: null }
    ) {
      returning {
        ...RecordFields
      }
    }
  }
`)

// Record browser queries =======================================================

// Column-only fetch for the update path. Planning a scalar/data patch needs
// the record's columns (data included via RecordFields) and none of its
// relation rows — fetching them anyway made a batch of column renames issue
// one relation-hydrating read per record. Relation patches go through
// fetchRecordById instead; the planner's hydration guard backstops any
// misclassification.
// The lean read for many records at once — updateMany's planning read.
export const fetchRecordsLeanByIds = graphql(`
  query fetchRecordsLeanByIds($ids: [Int!]!) {
    records(where: { id: { _in: $ids } }) {
      ...RecordFields
    }
  }
`)

/** The records of one type whose email contact is any of the addresses, each with
 *  up to three organizations it links to. `value` is the stored contact value, so
 *  the addresses must be lowercased to match. */
export const fetchRecordsByEmails = graphql(`
  query fetchRecordsByEmails($emails: [String!]!, $type: String!) {
    record_contacts(
      where: {
        value: { _in: $emails }
        contact_type: { _eq: "email" }
        record: { type: { _eq: $type } }
      }
    ) {
      value
      record {
        ...RecordFields
        links(where: { linked: { type: { _eq: "organization" } } }, limit: 3) {
          linked {
            id
            name
            slug
            type
          }
        }
      }
    }
  }
`)

export const fetchRecordLeanById = graphql(`
  query fetchRecordLeanById($id: Int!) {
    record: records_by_pk(id: $id) {
      ...RecordFields
    }
  }
`)

export const fetchRecordsByIds = graphql(`
  query fetchRecordsByIds($ids: [Int!]!) {
    records(where: { id: { _in: $ids } }, order_by: { name: asc }) {
      ...RecordFields
      taggings {
        ...RecordTaggingFields
      }
      contacts {
        ...RecordContactFields
      }
      events {
        ...RecordFields
      }
      organizations {
        ...RecordFields
      }
    }
  }
`)
